import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaGooglePlay } from 'react-icons/fa';
import SectionTitle from '../components/SectionTitle';
import Button from '../components/Button';
import QuickRoll from './QuickRoll.jpg';
import DigiToken from './DigiToken.jpg';
import hacko from './Hacko.png';
import Simon from './Simon.png';
import airbnb from './airbnb.png';
import spotify from './spotify.png';
import QueryGen from './QueryGen.png';
import neon from './neon.jpg';
import zip from './zip.jpg';

const Projects = () => {
  const [filter, setFilter] = useState('all');

  const projectsData = [
    {
      id: 1,
      title: 'QuickRoll — Enterprise Attendance',
      category: 'fullstack',
      image: QuickRoll,
      description: 'Full-stack attendance system deployed on AWS EC2 with Nginx & Redis caching, supporting 7,000+ students. Features serverless face verification via AWS Lambda + DeepFace, CloudFront CDN, and Amazon SES for OTP delivery.',
      technologies: ['MERN', 'AWS', 'Redis', 'Nginx', 'DeepFace', 'Socket.io'],
      github: 'https://github.com/HimanshuRawat143',
      demo: 'https://app.quickrollattendance.live/',
      featured: true,
    },
    {
      id: 2,
      title: 'QueryGen — AI Text-to-SQL',
      category: 'ai',
      image: QueryGen,
      description: 'Fine-tuned a T5-Base Transformer on the Spider dataset to translate natural language into executable SQL queries, achieving 85% accuracy on complex nested queries. Built a schema-aware React interface with active learning feedback loop.',
      technologies: ['Python', 'Transformers', 'T5', 'React.js', 'NLP'],
      github: 'https://github.com/HimanshuRawat143',
      demo: null,
      featured: true,
    },
    {
      id: 3,
      title: 'DigiToken — Secure Entry System',
      category: 'fullstack',
      image: DigiToken,
      description: 'Fraud-proof entry system using AES-256 encrypted QR codes, preventing ticket duplication. Real-time admin dashboard with Socket.io tracking live entry counts with <20ms latency. Reduced queue times by 50%.',
      technologies: ['MERN', 'AES-256', 'Socket.io', 'QR Codes'],
      github: 'https://github.com/HimanshuRawat143',
      demo: 'https://gehu-digitoken.vercel.app/',
      featured: true,
    },
    {
      id: 4,
      title: 'Hack-O-Holic 3.0',
      category: 'frontend',
      image: hacko,
      description: 'Complete event management website for Co-Dev Club\'s flagship 24-hour hackathon including event details, team registration, live updates, and responsive design for 200+ participants.',
      technologies: ['React', 'Framer Motion', 'Styled Components'],
      github: 'https://github.com/HimanshuRawat143',
      demo: 'https://hack-o-holic-3-0-i72w.onrender.com/',
      featured: false,
    },
    {
      id: 5,
      title: 'Airbnb Clone',
      category: 'fullstack',
      image: airbnb,
      description: 'Full-stack Airbnb clone with sign-up/login functionality, listing management, and responsive UI replicating the platform\'s design language.',
      technologies: ['React', 'Node.js', 'MongoDB', 'Express'],
      github: 'https://github.com/HimanshuRawat143',
      demo: 'https://wanderlust-hr.onrender.com/',
      featured: false,
    },
    {
      id: 6,
      title: 'Spotify Clone',
      category: 'frontend',
      image: spotify,
      description: 'Frontend clone of the Spotify web player with responsive layout, playlist UI, and audio player controls replicating the original interface.',
      technologies: ['HTML', 'CSS', 'JavaScript'],
      github: 'https://github.com/HimanshuRawat143',
      demo: 'https://spotify-hr.vercel.app/',
      featured: false,
    },
    {
      id: 7,
      title: 'Simon Says Game',
      category: 'frontend',
      image: Simon,
      description: 'Interactive memory game with color-based patterns and sound effects. Tests and improves memory skills through increasingly complex sequences.',
      technologies: ['JavaScript', 'HTML', 'CSS'],
      github: 'https://github.com/HimanshuRawat143',
      demo: 'https://simon-says-game-himanshu.vercel.app',
      featured: false,
    },
    {
      id: 8,
      title: 'Neon Dodge Game',
      category: 'frontend',
      image: neon,
      description: 'A fast-paced neon-themed dodge game built with vanilla web technologies. Features dynamic obstacle generation, score tracking, and vibrant neon aesthetics.',
      technologies: ['HTML', 'CSS', 'JavaScript'],
      github: 'https://github.com/HimanshuRawat143',
      demo: 'https://gray-pebble-0361b5d1e.2.azurestaticapps.net',
      featured: false,
    },
    {
      id: 9,
      title: 'Zip Game',
      category: 'frontend',
      image: zip,
      description: 'Developed the ZIP game inspired from LinkedIn daily ZIP Solve challenge. Interactive puzzle gameplay with clean UI and engaging mechanics.',
      technologies: ['React', 'CSS', 'JavaScript'],
      github: 'https://github.com/HimanshuRawat143',
      demo: 'https://zip-game-himanshu.vercel.app',
      featured: false,
    },
  ];

  const otherProjects = [
    {
      id: 101,
      title: 'Math Magic Web Game',
      description: 'Interactive math trick game using complementary number logic. Features real-time input handling, automatic calculations, and responsive design.',
      technologies: ['React', 'JavaScript', 'CSS'],
      github: 'https://github.com/HimanshuRawat143',
      demo: 'https://magic-addition.vercel.app/',
    },
    {
      id: 102,
      title: 'Binary Number Guessing',
      description: 'Interactive app that guesses a user\'s chosen number (1–31) using binary logic and visual cards with bitwise addition.',
      technologies: ['JavaScript', 'React', 'Bootstrap'],
      github: 'https://github.com/HimanshuRawat143',
      demo: 'https://number-prediction-game.vercel.app/',
    },
  ];

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'fullstack', label: 'Full Stack' },
    { key: 'frontend', label: 'Frontend' },
    { key: 'ai', label: 'AI / ML' },
  ];

  const filteredProjects = filter === 'all'
    ? projectsData
    : projectsData.filter(p => p.category === filter);

  const featuredProjects = filteredProjects.filter(p => p.featured);
  const regularProjects = filteredProjects.filter(p => !p.featured);

  return (
    <ProjectsSection id="projects">
      <div className="container">
        <SectionTitle subtitle="Portfolio" title="Featured Projects" />

        <FilterRow>
          {filters.map(f => (
            <FilterPill
              key={f.key}
              active={filter === f.key}
              onClick={() => setFilter(f.key)}
              as={motion.button}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {f.label}
            </FilterPill>
          ))}
        </FilterRow>

        {/* Featured Projects — Large Cards */}
        <AnimatePresence mode="wait">
          <FeaturedGrid
            as={motion.div}
            key={filter}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            {featuredProjects.map((project, i) => (
              <FeaturedCard
                key={project.id}
                as={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                {project.image && (
                  <FeaturedImageBox>
                    <FeaturedImg src={project.image} alt={project.title} />
                    <ImageOverlay />
                  </FeaturedImageBox>
                )}
                <FeaturedContent hasImage={!!project.image}>
                  <FeaturedLabel>Featured Project</FeaturedLabel>
                  <FeaturedTitle>{project.title}</FeaturedTitle>
                  <FeaturedDesc>{project.description}</FeaturedDesc>
                  <FeaturedTags>
                    {project.technologies.map(tech => (
                      <FeaturedTag key={tech}>{tech}</FeaturedTag>
                    ))}
                  </FeaturedTags>
                  <FeaturedLinks>
                    <IconLink href={project.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                      <FaGithub />
                    </IconLink>
                    {project.demo && (
                      <IconLink href={project.demo} target="_blank" rel="noopener noreferrer" aria-label="Live Demo">
                        <FaExternalLinkAlt />
                      </IconLink>
                    )}
                  </FeaturedLinks>
                </FeaturedContent>
              </FeaturedCard>
            ))}
          </FeaturedGrid>
        </AnimatePresence>

        {/* Regular Project Cards */}
        {regularProjects.length > 0 && (
          <RegularGrid>
            {regularProjects.map((project, i) => (
              <RegularCard
                key={project.id}
                as={motion.div}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                {project.image && (
                  <RegularImageBox>
                    <RegularImg src={project.image} alt={project.title} />
                    <RegularOverlay>
                      <OverlayLinks>
                        <OverlayLink href={project.github} target="_blank" rel="noopener noreferrer">
                          <FaGithub />
                        </OverlayLink>
                        {project.demo && (
                          <OverlayLink href={project.demo} target="_blank" rel="noopener noreferrer">
                            <FaExternalLinkAlt />
                          </OverlayLink>
                        )}
                      </OverlayLinks>
                    </RegularOverlay>
                  </RegularImageBox>
                )}
                <RegularContent>
                  <RegularTitle>{project.title}</RegularTitle>
                  <RegularDesc>{project.description}</RegularDesc>
                  <RegularTags>
                    {project.technologies.map(tech => (
                      <RegularTag key={tech}>{tech}</RegularTag>
                    ))}
                  </RegularTags>
                </RegularContent>
              </RegularCard>
            ))}
          </RegularGrid>
        )}

        {/* Other Projects */}
        <OtherSection>
          <OtherTitle>Other Noteworthy Projects</OtherTitle>
          <OtherGrid>
            {otherProjects.map((project, i) => (
              <OtherCard
                key={project.id}
                as={motion.div}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -6 }}
              >
                <OtherCardHeader>
                  <OtherFolderIcon>📁</OtherFolderIcon>
                  <OtherCardLinks>
                    <SmallLink href={project.github} target="_blank" rel="noopener noreferrer"><FaGithub /></SmallLink>
                    {project.demo && <SmallLink href={project.demo} target="_blank" rel="noopener noreferrer"><FaExternalLinkAlt /></SmallLink>}
                  </OtherCardLinks>
                </OtherCardHeader>
                <OtherCardTitle>{project.title}</OtherCardTitle>
                <OtherCardDesc>{project.description}</OtherCardDesc>
                <OtherCardTags>
                  {project.technologies.map(tech => (
                    <RegularTag key={tech}>{tech}</RegularTag>
                  ))}
                </OtherCardTags>
              </OtherCard>
            ))}
          </OtherGrid>
          <MoreBtn>
            <Button href="https://github.com/HimanshuRawat143" variant="outline">
              See More on GitHub <FaGithub />
            </Button>
          </MoreBtn>
        </OtherSection>
      </div>
    </ProjectsSection>
  );
};

// ===== STYLES =====

const ProjectsSection = styled.section``;

const FilterRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.6rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
`;

const FilterPill = styled.button`
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  font-family: ${props => props.theme.fonts.body};
  transition: all 0.3s ease;
  
  background: ${props => props.active
    ? props.theme.colors.gradientPrimary
    : 'rgba(255, 255, 255, 0.03)'};
  color: ${props => props.active ? 'white' : props.theme.colors.textSecondary};
  border: 1px solid ${props => props.active
    ? 'transparent'
    : 'rgba(255, 255, 255, 0.08)'};
  
  ${props => props.active && `
    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
  `}
`;

// --- Featured ---
const FeaturedGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  margin-bottom: 3rem;
`;

const FeaturedCard = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 2rem;
  background: ${props => props.theme.card.background};
  border: ${props => props.theme.card.border};
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(99, 102, 241, 0.2);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  }

  &:nth-child(even) {
    direction: rtl;
    > * { direction: ltr; }
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    &:nth-child(even) {
      direction: ltr;
    }
  }
`;

const FeaturedImageBox = styled.div`
  position: relative;
  overflow: hidden;
  min-height: 280px;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    min-height: 200px;
    max-height: 220px;
  }
`;

const FeaturedImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
  
  ${FeaturedCard}:hover & {
    transform: scale(1.05);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(6, 182, 212, 0.05));
  transition: opacity 0.3s ease;
`;

const FeaturedContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.8rem;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    padding: 1.2rem 1rem;
    gap: 0.6rem;
  }
`;

const FeaturedLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  text-transform: uppercase;
  letter-spacing: 2px;
  font-family: ${props => props.theme.fonts.mono};
`;

const FeaturedTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 800;
  color: ${props => props.theme.colors.text};
  line-height: 1.3;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: 1.2rem;
  }
`;

const FeaturedDesc = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.7;
`;

const FeaturedTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const FeaturedTag = styled.span`
  font-size: 0.7rem;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba(99, 102, 241, 0.1);
  color: ${props => props.theme.colors.primaryLight};
  font-family: ${props => props.theme.fonts.mono};
  font-weight: 500;
`;

const FeaturedLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const IconLink = styled.a`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
`;

// --- Regular ---
const RegularGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const RegularCard = styled.div`
  background: ${props => props.theme.card.background};
  border: ${props => props.theme.card.border};
  border-radius: 14px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(99, 102, 241, 0.2);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
  }
`;

const RegularImageBox = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
`;

const RegularImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${RegularCard}:hover & {
    transform: scale(1.08);
  }
`;

const RegularOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(3, 0, 20, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${RegularCard}:hover & {
    opacity: 1;
  }
`;

const OverlayLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const OverlayLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${props => props.theme.colors.gradientPrimary};
  color: white;
  font-size: 1.1rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const RegularContent = styled.div`
  padding: 1.3rem 1.5rem;
`;

const RegularTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.5rem;
`;

const RegularDesc = styled.p`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textMuted};
  line-height: 1.6;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RegularTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const RegularTag = styled.span`
  font-size: 0.7rem;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba(99, 102, 241, 0.08);
  color: ${props => props.theme.colors.primaryLight};
  font-family: ${props => props.theme.fonts.mono};
  font-weight: 500;
`;

// --- Other ---
const OtherSection = styled.div`
  margin-top: 1rem;
`;

const OtherTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  text-align: center;
  margin-bottom: 2rem;
`;

const OtherGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.2rem;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;
const OtherCard = styled.div`
  background: ${props => props.theme.card.background};
  border: ${props => props.theme.card.border};
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: rgba(99, 102, 241, 0.15);
  }
`;

const OtherCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const OtherFolderIcon = styled.span`
  font-size: 1.8rem;
`;

const OtherCardLinks = styled.div`
  display: flex;
  gap: 0.8rem;
`;

const SmallLink = styled.a`
  color: ${props => props.theme.colors.textMuted};
  font-size: 1rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const OtherCardTitle = styled.h4`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.6rem;
`;

const OtherCardDesc = styled.p`
  font-size: 0.82rem;
  color: ${props => props.theme.colors.textMuted};
  line-height: 1.6;
  margin-bottom: 1rem;
  flex: 1;
`;

const OtherCardTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const MoreBtn = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2.5rem;
`;

export default Projects;
